import { useState } from 'react'
import { defaultUnitFor } from '../../../utils/caseEntries'
import type { CaseEntry, CaseEntryType, CaseEntryUnit } from '../../../types/caseEntry'

export interface EntryFormState {
  type: CaseEntryType
  date: string
  description: string
  quantity: string
  unit: CaseEntryUnit
  unitPrice: string
  purchasePrice: string
  vatRate: string
}

export function todayIso(): string {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
}

/** Accepterer både dansk komma og punktum: "3,5" → 3.5 */
export function parseAmount(value: string): number {
  const cleaned = value.replace(/\s/g, '').replace(/\./g, (match, offset, full) =>
    // "1.575,50" → tusindtalsseparator fjernes, når der også er komma
    full.includes(',') ? '' : match,
  )
  const number = Number(cleaned.replace(',', '.'))
  return Number.isFinite(number) ? number : NaN
}

export function toInput(value: number | undefined): string {
  if (value === undefined || !Number.isFinite(value)) return ''
  return String(value).replace('.', ',')
}

export function useCaseEntryForm(defaultHourlyRate: string) {
  const blank = (type: CaseEntryType): EntryFormState => ({
    type,
    date: todayIso(),
    description: '',
    quantity: type === 'Servicevogn og værktøj' ? '1' : '',
    unit: defaultUnitFor[type],
    unitPrice: type === 'Timer' ? defaultHourlyRate.replace('.', ',') : '',
    purchasePrice: '',
    vatRate: '25',
  })

  const [form, setForm] = useState<EntryFormState>(() => blank('Timer'))
  const [editingId, setEditingId] = useState<string | null>(null)
  const [formError, setFormError] = useState('')

  function setField<K extends keyof EntryFormState>(key: K, value: EntryFormState[K]) {
    setForm((current) => ({ ...current, [key]: value }))
    setFormError('')
  }

  function chooseType(type: CaseEntryType) {
    // Skift af type nulstiller formularen, men bevarer datoen.
    setForm((current) => ({ ...blank(type), date: current.date }))
    setEditingId(null)
    setFormError('')
  }

  function startEdit(entry: CaseEntry) {
    setForm({
      type: entry.type,
      date: entry.date,
      description: entry.description,
      quantity: toInput(entry.quantity),
      unit: entry.unit,
      unitPrice: toInput(entry.unitPrice),
      purchasePrice: toInput(entry.purchasePrice),
      vatRate: String(entry.vatRate ?? 25),
    })
    setEditingId(entry.id)
    setFormError('')
  }

  function reset() {
    setForm((current) => blank(current.type))
    setEditingId(null)
    setFormError('')
  }

  /** Validerer formularen og returnerer en færdig linje — eller null ved fejl. */
  function build(existing?: CaseEntry): CaseEntry | null {
    const quantity = parseAmount(form.quantity || '1')
    const unitPrice = parseAmount(form.unitPrice)
    const purchase = form.purchasePrice.trim() ? parseAmount(form.purchasePrice) : undefined

    if (!form.description.trim()) {
      setFormError('Skriv en kort beskrivelse.')
      return null
    }
    if (!Number.isFinite(quantity) || quantity <= 0) {
      setFormError(form.type === 'Timer' ? 'Angiv antal timer, fx 3,5.' : 'Angiv et antal større end 0.')
      return null
    }
    if (!Number.isFinite(unitPrice) || unitPrice < 0) {
      setFormError('Angiv en gyldig pris.')
      return null
    }
    if (purchase !== undefined && !Number.isFinite(purchase)) {
      setFormError('Indkøbsprisen er ikke et gyldigt tal.')
      return null
    }

    const now = new Date().toISOString()
    return {
      id: existing?.id ?? `entry-${Math.random().toString(36).slice(2, 10)}`,
      type: form.type,
      date: form.date,
      description: form.description.trim(),
      quantity,
      unit: form.type === 'Timer' ? 'timer' : form.unit,
      unitPrice,
      ...(purchase !== undefined ? { purchasePrice: purchase } : {}),
      vatRate: Number(form.vatRate) || 0,
      ...(existing?.quoteLineId ? { quoteLineId: existing.quoteLineId } : {}),
      createdAt: existing?.createdAt ?? now,
      updatedAt: now,
    }
  }

  return { form, setField, chooseType, startEdit, reset, build, editingId, formError, setFormError }
}
